/**
 * Modal — responsive overlay panel.
 *
 * Desktop: centered card with maxWidth.
 * Mobile:  full-width sheet anchored to bottom of screen (easier to reach
 *          on a phone), with max-height scroll.
 *
 * Usage:
 *   <Modal title="Edit Patient" onClose={() => setOpen(false)}>
 *     <form>…</form>
 *   </Modal>
 */
import React, { useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';

export default function Modal({ title, onClose, children, width = 560, fullscreen = false }) {
  const isMobile = useIsMobile();

  // Close on Escape key
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // `fullscreen` renders a LARGE CENTERED dialog, not edge-to-edge — the
  // sidebar/topbar stay visible behind the dimmed backdrop so users keep
  // their navigation context. Accepts number (px) or string widths.
  const maxW = fullscreen
    ? 'min(1240px, calc(100vw - 64px))'
    : (typeof width === 'number' ? `${width}px` : width);

  const sheetStyle = isMobile
    ? {
        // Bottom-sheet on mobile
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff',
        borderRadius: '16px 16px 0 0',
        maxHeight: '92vh',
        overflowY: 'auto',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
        // Smooth slide-up
        animation: 'hhms-sheet-up 0.22s cubic-bezier(0.4,0,0.2,1)',
      }
    : {
        // Centered card on desktop ('fullscreen' = wide variant of the same)
        background: '#fff',
        borderRadius: 18,
        width: '100%',
        maxWidth: maxW,
        maxHeight: fullscreen ? '92vh' : '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(13,27,46,0.2)',
        margin: '0 16px',
        animation: 'hhms-modal-in 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
      };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(13,27,46,0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: isMobile ? 'stretch' : 'center',
        zIndex: 1000,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={sheetStyle}>

        {/* Drag handle (mobile visual affordance) */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 2px' }}>
            <div style={{ width: 36, height: 4, background: '#cbd5e1', borderRadius: 2 }} />
          </div>
        )}

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: '1px solid #ece3e7',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
          flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? 15 : 16, fontWeight: 700, color: '#2b1b22' }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: '#f1f5f9', border: 'none', fontSize: 18,
              cursor: 'pointer', color: '#8b7681', lineHeight: 1,
              padding: '4px 8px', borderRadius: 6, flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: isMobile ? '14px 16px 24px' : (fullscreen ? '24px 32px' : '20px 24px'),
          flex: fullscreen ? 1 : undefined,
          minHeight: 0,
        }}>
          {children}
        </div>

      </div>
    </div>
  );
}
